<template>
  <div class="schedule-page">
    <el-card>
      <div slot="header" class="card-header">
        <span>定时调度管理</span>
        <el-button type="primary" size="small" @click="handleAdd">新增调度</el-button>
      </div>

      <el-table :data="scheduleList" border stripe v-loading="loading">
        <el-table-column label="执行时间" width="140" align="center">
          <template slot-scope="scope">
            <div class="exec-time-cell">
              <template v-if="scope.row._execTime">
                <div class="lcd-display">
                  <span class="lcd-ghost" aria-hidden="true">88:88</span>
                  <span class="lcd-value">{{ scope.row._execTime }}</span>
                </div>
              </template>
              <template v-else>
                <div class="exec-time-freq">{{ scope.row._execFreqText || '自定义' }}</div>
              </template>
            </div>
          </template>
        </el-table-column>
        <el-table-column label="任务名称" width="150">
          <template slot-scope="scope">
            <span class="task-label-text">{{ scope.row.taskLabel }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="taskDesc" label="说明" min-width="200" show-overflow-tooltip></el-table-column>
        <el-table-column prop="cron" label="cron 表达式" width="180">
          <template slot-scope="scope">
            <code>{{ scope.row.cron }}</code>
            <div v-if="cronToText(scope.row.cron)" style="font-size: 12px; color: #909399; margin-top: 4px;">
              {{ cronToText(scope.row.cron) }}
            </div>
          </template>
        </el-table-column>
        <el-table-column label="状态" width="80" align="center">
          <template slot-scope="scope">
            <el-switch
              v-model="scope.row.enabled"
              @change="handleToggle(scope.row)"
              :disabled="toggling"
            ></el-switch>
          </template>
        </el-table-column>
        <el-table-column label="上次执行" width="260">
          <template slot-scope="scope">
            <div v-if="scope.row.lastRunAt">
              <div>
                <el-tag
                  :type="scope.row.lastStatus === 'success' ? 'success' : 'danger'"
                  size="mini"
                >{{ scope.row.lastStatus === 'success' ? '成功' : '失败' }}</el-tag>
                <span style="margin-left: 5px; font-size: 12px; color: #999;">
                  {{ scope.row.lastRunAt | dateFormat }}
                </span>
              </div>
              <div v-if="formatLastResult(scope.row)" class="last-result-text">
                {{ formatLastResult(scope.row) }}
              </div>
            </div>
            <span v-else style="color: #ccc;">未执行</span>
          </template>
        </el-table-column>
        <el-table-column label="下次执行" width="180">
          <template slot-scope="scope">
            <span v-if="scope.row.enabled && scope.row.nextRunTime" style="font-size: 12px;">
              {{ scope.row.nextRunTime | dateFormat }}
            </span>
            <span v-else style="color: #ccc;">—</span>
          </template>
        </el-table-column>
        <el-table-column prop="remark" label="备注" min-width="120" show-overflow-tooltip></el-table-column>
        <el-table-column label="操作" width="240" fixed="right">
          <template slot-scope="scope">
            <el-button size="mini" type="success" @click="handleTest(scope.row)" :loading="testing">
              手动执行
            </el-button>
            <el-button size="mini" @click="handleEdit(scope.row)">编辑</el-button>
            <el-button size="mini" type="danger" @click="handleDelete(scope.row)">删除</el-button>
          </template>
        </el-table-column>
      </el-table>
    </el-card>

    <!-- 新增/编辑弹窗 -->
    <el-dialog :title="dialogTitle" :visible.sync="dialogVisible" width="600px">
      <el-form :model="formData" label-width="120px" :rules="formRules" ref="scheduleForm">
        <el-form-item label="选择任务" prop="taskName">
          <el-select
            v-model="formData.taskName"
            placeholder="请选择任务"
            style="width: 100%"
            :disabled="isEdit"
            @change="onTaskChange"
          >
            <el-option
              v-for="item in registryList"
              :key="item.key"
              :label="item.name"
              :value="item.key"
            >
              <span>{{ item.name }}</span>
              <span style="float: right; color: #8492a6; font-size: 12px;">{{ item.description }}</span>
            </el-option>
          </el-select>
          <div v-if="selectedTaskDesc" style="font-size: 12px; color: #909399; margin-top: 4px;">
            {{ selectedTaskDesc }}
          </div>
        </el-form-item>

        <el-form-item label="执行频率" prop="cron">
          <!-- 可视化模式 -->
          <div v-if="!advancedCronMode" class="cron-builder">
            <el-select v-model="cronConfig.frequency" placeholder="选择频率" style="width: 100%; margin-bottom: 10px;" @change="generateCron">
              <el-option label="每天" value="daily"></el-option>
              <el-option label="每周" value="weekly"></el-option>
              <el-option label="每小时" value="hourly"></el-option>
              <el-option label="每隔N分钟" value="minutes"></el-option>
            </el-select>

            <!-- 每天 / 每周：时间选择器 -->
            <div v-if="cronConfig.frequency === 'daily' || cronConfig.frequency === 'weekly'">
              <el-time-picker
                v-model="cronConfig.time"
                placeholder="选择执行时间"
                format="HH:mm"
                value-format="HH:mm"
                style="width: 200px;"
                @change="generateCron"
              ></el-time-picker>
              <span v-if="cronConfig.frequency === 'daily'" style="margin-left: 8px; font-size: 13px; color: #909399;">每天此时间执行</span>
            </div>

            <!-- 每周：选择周几 -->
            <div v-if="cronConfig.frequency === 'weekly'" style="margin-top: 10px;">
              <el-checkbox-group v-model="cronConfig.weekdays" :min="1" @change="generateCron">
                <el-checkbox v-for="day in weekdayOptions" :key="day.value" :label="day.value">{{ day.label }}</el-checkbox>
              </el-checkbox-group>
            </div>

            <!-- 每小时 -->
            <div v-if="cronConfig.frequency === 'hourly'">
              <span style="font-size: 13px; color: #909399;">每整点执行一次</span>
            </div>

            <!-- 每隔N分钟 -->
            <div v-if="cronConfig.frequency === 'minutes'">
              <span>每隔</span>
              <el-input-number v-model="cronConfig.minuteInterval" :min="1" :max="59" size="small" style="width: 100px; margin: 0 8px;" @change="generateCron"></el-input-number>
              <span>分钟执行一次</span>
            </div>

            <!-- 生成的 cron 预览 -->
            <div class="cron-preview">
              <i class="el-icon-time"></i>
              <span>生成的 cron 表达式：</span>
              <code>{{ formData.cron || '请配置' }}</code>
              <span v-if="cronDescription" style="color: #909399; margin-left: 8px;">（{{ cronDescription }}）</span>
            </div>
          </div>

          <!-- 高级模式：直接写 cron -->
          <div v-else>
            <el-input v-model="formData.cron" placeholder="如：0 0 3 * * *"></el-input>
            <div style="font-size: 12px; color: #909399; margin-top: 4px;">
              6段格式：秒 分 时 日 月 周
            </div>
          </div>

          <!-- 模式切换 -->
          <div style="margin-top: 6px;">
            <el-button type="text" size="mini" @click="toggleCronMode">
              {{ advancedCronMode ? '切换到可视化模式' : '高级模式（手写 cron）' }}
            </el-button>
          </div>
        </el-form-item>

        <el-form-item label="启用">
          <el-switch v-model="formData.enabled"></el-switch>
        </el-form-item>

        <el-form-item label="备注">
          <el-input v-model="formData.remark" type="textarea" :rows="2"></el-input>
        </el-form-item>
      </el-form>

      <div slot="footer">
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" @click="handleSubmit" :loading="submitting">确定</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>

export default {
  name: 'Schedule',
  data () {
    return {
      loading: false,
      toggling: false,
      testing: false,
      submitting: false,
      scheduleList: [],
      registryList: [],
      dialogVisible: false,
      isEdit: false,
      editingId: null,
      formData: {
        taskName: '',
        cron: '',
        enabled: false,
        remark: ''
      },
      formRules: {
        taskName: [{ required: true, message: '请选择任务', trigger: 'change' }],
        cron: [{ required: true, message: '请配置执行频率', trigger: 'change' }]
      },
      // cron 可视化配置
      advancedCronMode: false,
      cronConfig: {
        frequency: '', // daily/weekly/hourly/minutes
        time: '03:00', // HH:mm
        weekdays: [1], // 0-6 (0=周日)
        minuteInterval: 30
      },
      weekdayOptions: [
        { label: '周日', value: 0 },
        { label: '周一', value: 1 },
        { label: '周二', value: 2 },
        { label: '周三', value: 3 },
        { label: '周四', value: 4 },
        { label: '周五', value: 5 },
        { label: '周六', value: 6 }
      ]
    }
  },
  computed: {
    dialogTitle () {
      return this.isEdit ? '编辑调度' : '新增调度'
    },
    selectedTaskDesc () {
      const task = this.registryList.find(t => t.key === this.formData.taskName)
      return task ? task.description : ''
    },
    cronDescription () {
      return this.buildCronDescription(this.cronConfig)
    }
  },
  created () {
    this.fetchList()
    this.fetchRegistry()
  },
  methods: {
    /**
     * 根据可视化配置生成 cron 表达式
     * 格式：秒 分 时 日 月 周
     */
    generateCron () {
      const cfg = this.cronConfig
      if (!cfg.frequency) {
        this.formData.cron = ''
        return
      }
      const parts = ['0', '*', '*', '*', '*', '*']

      if (cfg.frequency === 'daily') {
        const [h, m] = (cfg.time || '00:00').split(':')
        parts[1] = parseInt(m)
        parts[2] = parseInt(h)
      } else if (cfg.frequency === 'weekly') {
        const [h, m] = (cfg.time || '00:00').split(':')
        parts[1] = parseInt(m)
        parts[2] = parseInt(h)
        parts[5] = cfg.weekdays.length > 0 ? cfg.weekdays.sort().join(',') : '*'
      } else if (cfg.frequency === 'hourly') {
        // 0 0 * * * *（每整点0分0秒）
        parts[1] = 0
      } else if (cfg.frequency === 'minutes') {
        // 0 */N * * * *
        parts[1] = `*/${cfg.minuteInterval || 30}`
      }

      this.formData.cron = parts.join(' ')
    },

    /**
     * 将 cron 表达式逆向解析为可视化配置
     */
    parseCronToConfig (cronStr) {
      if (!cronStr) {
        this.cronConfig.frequency = ''
        return
      }
      const parts = cronStr.trim().split(/\s+/)
      // 尝试解析，如果无法识别则回退到高级模式
      const sec = parts[0]; const min = parts[1]; const hour = parts[2]; const day = parts[3]; const mon = parts[4]; const week = parts[5]

      // 每天：0 M H * * *
      if (sec === '0' && day === '*' && mon === '*' && week === '*' && !min.includes('*') && !min.includes('/') && !hour.includes('*')) {
        this.cronConfig.frequency = 'daily'
        this.cronConfig.time = `${String(hour).padStart(2, '0')}:${String(min).padStart(2, '0')}`
        return
      }
      // 每周：0 M H * * W
      if (sec === '0' && day === '*' && mon === '*' && week !== '*' && !min.includes('*') && !min.includes('/') && !hour.includes('*')) {
        this.cronConfig.frequency = 'weekly'
        this.cronConfig.time = `${String(hour).padStart(2, '0')}:${String(min).padStart(2, '0')}`
        this.cronConfig.weekdays = week.split(',').map(Number)
        return
      }
      // 每小时：0 0 * * * *
      if (sec === '0' && min === '0' && hour === '*' && day === '*' && mon === '*' && week === '*') {
        this.cronConfig.frequency = 'hourly'
        return
      }
      // 每隔N分钟：0 */N * * * *
      if (sec === '0' && min.startsWith('*/') && hour === '*' && day === '*' && mon === '*' && week === '*') {
        this.cronConfig.frequency = 'minutes'
        this.cronConfig.minuteInterval = parseInt(min.substring(2))
        return
      }
      // 无法识别 → 高级模式
      this.advancedCronMode = true
    },

    /**
     * 生成人类可读的描述
     */
    buildCronDescription (cfg) {
      if (!cfg.frequency) return ''
      if (cfg.frequency === 'daily') {
        return `每天 ${cfg.time} 执行`
      }
      if (cfg.frequency === 'weekly') {
        const days = cfg.weekdays.map(d => this.weekdayOptions.find(w => w.value === d)?.label).join('、')
        return `每${days} ${cfg.time} 执行`
      }
      if (cfg.frequency === 'hourly') {
        return '每整点执行一次'
      }
      if (cfg.frequency === 'minutes') {
        return `每隔 ${cfg.minuteInterval} 分钟执行一次`
      }
      return ''
    },

    /**
     * 将 6 段 cron 表达式转为中文描述（纯函数，不修改组件状态）
     * 格式：秒 分 时 日 月 周
     * @param {string} cronStr
     * @return {string} 中文描述，无法识别时返回空字符串
     */
    cronToText (cronStr) {
      if (!cronStr || typeof cronStr !== 'string') return ''
      const parts = cronStr.trim().split(/\s+/)
      if (parts.length < 6) return ''
      const sec = parts[0]
      const min = parts[1]
      const hour = parts[2]
      const day = parts[3]
      const mon = parts[4]
      const week = parts[5]
      const pad = n => String(n).padStart(2, '0')
      const allStar = s => s === '*'
      const isNum = s => /^\d+$/.test(s)
      const isStep = s => /^\*\/\d+$/.test(s)

      // 每整点：0 0 * * * *
      if (sec === '0' && min === '0' && allStar(hour) && allStar(day) && allStar(mon) && allStar(week)) {
        return '每整点执行一次'
      }
      // 每 N 分钟：0 */N * * * *
      if (sec === '0' && isStep(min) && allStar(hour) && allStar(day) && allStar(mon) && allStar(week)) {
        const n = min.substring(2)
        return `每 ${n} 分钟执行一次`
      }
      // 每 N 小时：0 0 */N * * *
      if (sec === '0' && min === '0' && isStep(hour) && allStar(day) && allStar(mon) && allStar(week)) {
        const n = hour.substring(2)
        return `每 ${n} 小时执行一次`
      }
      // 每月定时：0 M H D * *
      if (sec === '0' && isNum(min) && isNum(hour) && isNum(day) && allStar(mon) && allStar(week)) {
        return `每月 ${day} 日 ${pad(hour)}:${pad(min)} 执行`
      }
      // 每周定时：0 M H * * W
      if (sec === '0' && isNum(min) && isNum(hour) && allStar(day) && allStar(mon) && week !== '*') {
        const weekMap = ['周日', '周一', '周二', '周三', '周四', '周五', '周六']
        const days = week.split(',').map(w => {
          const n = parseInt(w)
          const idx = n === 7 ? 0 : n
          return weekMap[idx] || ''
        }).filter(Boolean).join('、')
        return `每${days} ${pad(hour)}:${pad(min)} 执行`
      }
      // 每天定时：0 M H * * *
      if (sec === '0' && isNum(min) && isNum(hour) && allStar(day) && allStar(mon) && allStar(week)) {
        return `每天 ${pad(hour)}:${pad(min)} 执行`
      }
      return ''
    },

    /**
     * 将 6 段 cron 表达式解析为结构化执行时间信息（纯函数，不修改组件状态）
     * 格式：秒 分 时 日 月 周
     * @param {string} cronStr
     * @return {{time: string|null, badge: string, sortKey: number|null, freqText: string}}
     *   - time:     固定时间点 'HH:MM'，高频/无法识别返回 null
     *   - badge:    频率限定小字（每天/周一、周三/每月5日），高频任务为 ''
     *   - sortKey:  当天分钟数(0-1439)用于排序，无固定时间返回 null
     *   - freqText: 高频任务频率文案（每整点/每30分钟/每2小时），固定时间任务为 ''
     */
    parseCronTime (cronStr) {
      const empty = { time: null, badge: '', sortKey: null, freqText: '' }
      if (!cronStr || typeof cronStr !== 'string') return empty
      const parts = cronStr.trim().split(/\s+/)
      if (parts.length < 6) return empty
      const sec = parts[0]
      const min = parts[1]
      const hour = parts[2]
      const day = parts[3]
      const mon = parts[4]
      const week = parts[5]
      const pad = n => String(n).padStart(2, '0')
      const allStar = s => s === '*'
      const isNum = s => /^\d+$/.test(s)
      const isStep = s => /^\*\/\d+$/.test(s)
      const weekMap = ['周日', '周一', '周二', '周三', '周四', '周五', '周六']

      // 每整点：0 0 * * * *
      if (sec === '0' && min === '0' && allStar(hour) && allStar(day) && allStar(mon) && allStar(week)) {
        return { time: null, badge: '', sortKey: null, freqText: '每整点' }
      }
      // 每 N 分钟：0 */N * * * *
      if (sec === '0' && isStep(min) && allStar(hour) && allStar(day) && allStar(mon) && allStar(week)) {
        return { time: null, badge: '', sortKey: null, freqText: `每${min.substring(2)}分钟` }
      }
      // 每 N 小时：0 0 */N * * *
      if (sec === '0' && min === '0' && isStep(hour) && allStar(day) && allStar(mon) && allStar(week)) {
        return { time: null, badge: '', sortKey: null, freqText: `每${hour.substring(2)}小时` }
      }
      // 每月定时：0 M H D * *
      if (sec === '0' && isNum(min) && isNum(hour) && isNum(day) && allStar(mon) && allStar(week)) {
        return { time: `${pad(hour)}:${pad(min)}`, badge: `每月${day}日`, sortKey: parseInt(hour) * 60 + parseInt(min), freqText: '' }
      }
      // 每周定时：0 M H * * W
      if (sec === '0' && isNum(min) && isNum(hour) && allStar(day) && allStar(mon) && week !== '*') {
        const days = week.split(',').map(w => {
          const n = parseInt(w)
          const idx = n === 7 ? 0 : n
          return weekMap[idx] || ''
        }).filter(Boolean).join('、')
        return { time: `${pad(hour)}:${pad(min)}`, badge: `每${days}`, sortKey: parseInt(hour) * 60 + parseInt(min), freqText: '' }
      }
      // 每天定时：0 M H * * *
      if (sec === '0' && isNum(min) && isNum(hour) && allStar(day) && allStar(mon) && allStar(week)) {
        return { time: `${pad(hour)}:${pad(min)}`, badge: '每天', sortKey: parseInt(hour) * 60 + parseInt(min), freqText: '' }
      }
      // 无法识别
      return empty
    },

    toggleCronMode () {
      this.advancedCronMode = !this.advancedCronMode
      if (!this.advancedCronMode) {
        // 切回可视化时，尝试解析当前 cron
        this.parseCronToConfig(this.formData.cron)
        this.generateCron()
      }
    },

    async fetchList () {
      this.loading = true
      try {
        const { data: res } = await this.$http.get('schedule/list')
        if (res.meta.status === 200) {
          // 预计算执行时间信息并按固定时间点升序排序
          // 高频/无法识别的任务 sortKey 为 null，统一排在固定时间任务之后
          this.scheduleList = res.data
            .map(item => {
              const t = this.parseCronTime(item.cron)
              return {
                ...item,
                _execTime: t.time,
                _execBadge: t.badge,
                _execSortKey: t.sortKey,
                _execFreqText: t.freqText
              }
            })
            .sort((a, b) => {
              if (a._execSortKey != null && b._execSortKey != null) return a._execSortKey - b._execSortKey
              if (a._execSortKey != null) return -1
              if (b._execSortKey != null) return 1
              return 0 // 都无固定时间，保持稳定顺序
            })
        } else {
          this.$message.error(res.meta.msg || '获取列表失败')
        }
      } catch (err) {
        this.$message.error('获取列表失败')
      } finally {
        this.loading = false
      }
    },

    async fetchRegistry () {
      try {
        const { data: res } = await this.$http.get('schedule/registry')
        if (res.meta.status === 200) {
          this.registryList = res.data
        }
      } catch (err) {
        // 忽略
      }
    },

    formatLastResult (row) {
      const r = row.lastResult
      if (!r) return ''
      // 评价自动回复
      if (r.replied !== undefined) {
        return `回复${r.replied}条 / 失败${r.failed || 0}条`
      }
      // 日报/差评推送
      if (r.pushed !== undefined) {
        return `推送${r.pushed}条 / 失败${r.failed || 0}条`
      }
      // 群列表同步
      if (r.synced !== undefined) {
        return `同步${r.synced}个 / 共${r.total || 0}个`
      }
      // 采集任务（已投递队列）
      if (r.data && r.data.jobId) {
        return '已投递采集队列'
      }
      // 日报生成等
      if (r.total !== undefined) {
        return `处理${r.total}条`
      }
      return ''
    },

    handleAdd () {
      this.isEdit = false
      this.editingId = null
      this.formData = { taskName: '', cron: '', enabled: false, remark: '' }
      // 重置可视化配置
      this.advancedCronMode = false
      this.cronConfig = { frequency: '', time: '03:00', weekdays: [1], minuteInterval: 30 }
      this.dialogVisible = true
      this.$nextTick(() => {
        this.$refs.scheduleForm && this.$refs.scheduleForm.clearValidate()
      })
    },

    handleEdit (row) {
      this.isEdit = true
      this.editingId = row._id
      this.formData = {
        taskName: row.taskName,
        cron: row.cron,
        enabled: row.enabled,
        remark: row.remark || ''
      }
      // 尝试解析 cron 到可视化配置
      this.advancedCronMode = false
      this.cronConfig = { frequency: '', time: '03:00', weekdays: [1], minuteInterval: 30 }
      this.parseCronToConfig(row.cron)
      this.dialogVisible = true
      this.$nextTick(() => {
        this.$refs.scheduleForm && this.$refs.scheduleForm.clearValidate()
      })
    },

    onTaskChange () {
      // 选择任务时自动填充备注
      const task = this.registryList.find(t => t.key === this.formData.taskName)
      if (task && !this.formData.remark) {
        this.formData.remark = task.name
      }
    },

    async handleSubmit () {
      this.$refs.scheduleForm.validate(async valid => {
        if (!valid) return
        this.submitting = true
        try {
          let res
          if (this.isEdit) {
            res = await this.$http.put(`schedule/modify/${this.editingId}`, this.formData)
          } else {
            res = await this.$http.post('schedule/add', this.formData)
          }
          if (res.data.meta.status === 200) {
            this.$message.success(res.data.meta.msg || '操作成功')
            this.dialogVisible = false
            this.fetchList()
          } else {
            this.$message.error(res.data.meta.msg || '操作失败')
          }
        } catch (err) {
          this.$message.error('操作失败')
        } finally {
          this.submitting = false
        }
      })
    },

    async handleDelete (row) {
      try {
        await this.$confirm(`确定删除「${row.taskLabel || row.taskName}」调度配置？`, '提示', {
          type: 'warning'
        })
      } catch (e) {
        return
      }
      try {
        const { data: res } = await this.$http.delete(`schedule/remove/${row._id}`)
        if (res.meta.status === 200) {
          this.$message.success('删除成功')
          this.fetchList()
        } else {
          this.$message.error(res.meta.msg || '删除失败')
        }
      } catch (err) {
        this.$message.error('删除失败')
      }
    },

    async handleToggle (row) {
      this.toggling = true
      try {
        const { data: res } = await this.$http.put(`schedule/modify/${row._id}`, {
          taskName: row.taskName,
          cron: row.cron,
          enabled: row.enabled,
          remark: row.remark
        })
        if (res.meta.status === 200) {
          this.$message.success(row.enabled ? '已开启' : '已关闭')
        } else {
          this.$message.error(res.meta.msg || '操作失败')
          row.enabled = !row.enabled
        }
      } catch (err) {
        this.$message.error('操作失败')
        row.enabled = !row.enabled
      } finally {
        this.toggling = false
      }
    },

    async handleTest (row) {
      this.testing = true
      try {
        const { data: res } = await this.$http.post(`schedule/test/${row._id}`)
        if (res.meta.status === 200) {
          this.$message.success('执行完成')
          this.fetchList()
        } else {
          this.$message.error(res.meta.msg || '执行失败')
        }
      } catch (err) {
        this.$message.error('执行失败')
      } finally {
        this.testing = false
      }
    }
  }
}
</script>

<style scoped>
.schedule-page {
  padding: 0;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

code {
  background: #f5f7fa;
  padding: 2px 6px;
  border-radius: 3px;
  font-size: 13px;
  color: #e6a23c;
}

.cron-builder {
  padding: 4px 0;
}

.cron-preview {
  margin-top: 12px;
  padding: 8px 12px;
  background: #f0f9ff;
  border: 1px solid #d0e8ff;
  border-radius: 4px;
  font-size: 13px;
}

.cron-preview code {
  font-weight: bold;
  color: #409eff;
}

/* ===== 执行时间列：液晶屏 LCD + 青绿荧光 ===== */
.exec-time-cell {
  text-align: center;
  padding: 2px 0;
}

/* LCD 凹陷显示屏：深色底块 + 内阴影模拟玻璃凹陷面板 */
.lcd-display {
  position: relative;
  display: inline-block;
  padding: 5px 12px;
  border-radius: 4px;
  background: linear-gradient(180deg, #061410 0%, #0a1a14 50%, #061210 100%);
  border: 1px solid #0d2820;
  box-shadow:
    inset 0 2px 5px rgba(0, 0, 0, 0.85),
    inset 0 -1px 0 rgba(255, 255, 255, 0.03),
    inset 0 1px 0 rgba(255, 255, 255, 0.04);
  font-family: 'Consolas', 'DejaVu Sans Mono', 'Courier New', monospace;
  font-size: 22px;
  font-weight: 700;
  line-height: 1.1;
  letter-spacing: 1.5px;
  text-align: center;
  overflow: hidden;
}
/* 顶部玻璃高光：凹陷面板的材质光泽 */
.lcd-display::before {
  content: '';
  position: absolute;
  top: 0; left: 0; right: 0;
  height: 45%;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.06), transparent);
  border-radius: 4px 4px 0 0;
  pointer-events: none;
}
/* 幽灵段：未点亮的 88:88 暗影，真实数字盖上去后未点亮的段透出来（LCD 灵魂细节） */
.lcd-ghost {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(45, 255, 170, 0.07);
  pointer-events: none;
  user-select: none;
}
/* 真实数字：青绿荧光多层发光（紧贴→中层→外层→扩散） */
.lcd-value {
  position: relative;
  z-index: 1;
  color: #2dffaa;
  text-shadow:
    0 0 3px rgba(45, 255, 170, 0.9),
    0 0 8px rgba(45, 255, 170, 0.55),
    0 0 16px rgba(45, 255, 170, 0.3),
    0 0 28px rgba(45, 255, 170, 0.12);
}
.exec-time-badge {
  font-size: 12px;
  color: #6b8079;
  margin-top: 4px;
  letter-spacing: 0.3px;
}
/* 任务名：加粗 + 字号加大，强化主信息视觉权重 */
.task-label-text {
  font-weight: 700;
  font-size: 15px;
  color: #303133;
}
/* 高频/无固定时间：青绿荧光频率文案（弱发光，区别于主时间的强发光） */
.exec-time-freq {
  font-family: 'Consolas', 'DejaVu Sans Mono', 'Courier New', monospace;
  font-size: 15px;
  font-weight: 600;
  color: #2dffaa;
  text-shadow: 0 0 6px rgba(45, 255, 170, 0.3);
  line-height: 1.4;
  padding: 8px 0;
}
/* 上次执行结果统计文字 */
.last-result-text {
  margin-top: 4px;
  font-size: 12px;
  color: #606266;
}
</style>
